import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";

const batteryPoints = [
  {
    icon: "ri-battery-charge-line",
    title: "Battery Is a Critical Component",
    description:
      "The battery system is central to an EV retrofit. Its selection and integration shape how the converted vehicle performs in daily use.",
  },
  {
    icon: "ri-scales-3-line",
    title: "Selection Is Vehicle-Specific",
    description:
      "Battery selection depends on the vehicle, weight, duty cycle, expected usage and engineering requirements — not on a single universal specification.",
  },
  {
    icon: "ri-pulse-line",
    title: "Managed by the BMS",
    description:
      "The Battery Management System monitors and manages battery operation, supporting safe and reliable use across the working range.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Safety & Thermal First",
    description:
      "Safety and thermal considerations are essential. They are treated as engineering requirements throughout the retrofit process.",
  },
];

export default function BatterySection() {
  return (
    <section className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-container px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <SectionHeading
              eyebrow="Battery Technology"
              title="The Battery System Decides How the Retrofit Behaves"
              description="Battery configuration is configuration dependent. We do not publish a universal capacity, range or charging time, because the right configuration is determined during engineering assessment."
            />
            <div className="mt-8 space-y-4">
              {batteryPoints.map((point) => (
                <div key={point.title} className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                    <i className={`${point.icon} text-lg`} />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">{point.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                      {point.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <ActionButton to="/contact" variant="primary" size="lg" icon="ri-chat-3-line">
                Discuss Your Vehicle
              </ActionButton>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-lg border border-background-200">
              <div className="h-64 w-full md:h-[460px]">
                <img
                  src="https://readdy.ai/api/search-image?query=electric%20vehicle%20battery%20pack%20module%20close%20up%20on%20a%20clean%20neutral%20surface%2C%20metallic%20grey%20and%20subtle%20green%20accents%2C%20studio%20product%20photography%2C%20high%20detail%2C%20soft%20even%20lighting&width=1200&height=900&seq=klz-tech-01&orientation=landscape"
                  alt="Electric vehicle battery pack and battery management system close up"
                  title="EV retrofit battery system and BMS"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="grid grid-cols-2 gap-px bg-background-200">
                <div className="bg-background-50 p-5">
                  <p className="font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-500">
                    Capacity
                  </p>
                  <p className="mt-1 text-sm font-semibold text-foreground-900">Configuration dependent</p>
                </div>
                <div className="bg-background-50 p-5">
                  <p className="font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-500">
                    Range
                  </p>
                  <p className="mt-1 text-sm font-semibold text-foreground-900">Subject to assessment</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}